import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import { LayoutDashboard, Megaphone, Sparkles, Users, MessageSquare, FileText, Bell, LogOut, Globe, Menu, X, CreditCard } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";

const navItems = (t) => [
  { to: "/app", icon: LayoutDashboard, label: t("dashboard"), testId: "nav-dashboard", end: true },
  { to: "/app/posts", icon: Megaphone, label: t("social_posts"), testId: "nav-posts" },
  { to: "/app/ai", icon: Sparkles, label: t("ai_generator"), testId: "nav-ai" },
  { to: "/app/candidates", icon: Users, label: t("candidates"), testId: "nav-candidates" },
  { to: "/app/messaging", icon: MessageSquare, label: t("messaging"), testId: "nav-messaging" },
  { to: "/app/forms", icon: FileText, label: t("forms"), testId: "nav-forms" },
  { to: "/app/notifications", icon: Bell, label: t("notifications"), testId: "nav-notifications" },
  { to: "/app/billing", icon: CreditCard, label: t("billing"), testId: "nav-billing" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.filter((n) => !n.read).length);
    } catch { /* empty */ }
  };
  useEffect(() => { loadUnread(); const i = setInterval(loadUnread, 30000); return () => clearInterval(i); }, []);

  const onLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 z-40 h-screen w-64 bg-white border-r border-zinc-200 flex flex-col transition-transform`} data-testid="sidebar">
        <div className="p-6 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 klein-blue flex items-center justify-center font-bold text-white" style={{borderRadius: 2}}>R</div>
            <div>
              <div className="font-display font-bold text-lg leading-none">RecruitFlow</div>
              <div className="text-xs text-zinc-500 tracking-[0.2em] uppercase mt-1">AI</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems(t).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setOpen(false)}
              data-testid={it.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
              style={{ borderRadius: 2 }}
            >
              <it.icon size={18} strokeWidth={2} />
              <span>{it.label}</span>
              {it.to === "/app/notifications" && unread > 0 && (
                <span className="ml-auto text-[10px] font-bold bg-[#E63946] text-white px-1.5 py-0.5" data-testid="unread-badge">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-zinc-500 truncate">{user?.agency_name}</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
          </div>
          <button onClick={onLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors" style={{borderRadius: 2}}>
            <LogOut size={18} />
            <span>{t("logout")}</span>
          </button>
        </div>
      </aside>

      {open && <div className="md:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/70 backdrop-blur-xl border-b border-zinc-200/50">
          <div className="flex items-center justify-between px-4 md:px-8 h-16">
            <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X size={20}/> : <Menu size={20}/>}
            </button>
            <div className="hidden md:block text-xs uppercase tracking-[0.2em] text-zinc-500">
              {t("overview")}
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 px-3 py-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="lang-switcher" style={{borderRadius: 2}}>
                    <Globe size={14} />
                    <span className="uppercase">{lang}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="lang-fr" onClick={() => setLang("fr")}>Français</DropdownMenuItem>
                  <DropdownMenuItem data-testid="lang-en" onClick={() => setLang("en")}>English</DropdownMenuItem>
                  <DropdownMenuItem data-testid="lang-hu" onClick={() => setLang("hu")}>Magyar</DropdownMenuItem>
                  <DropdownMenuItem data-testid="lang-it" onClick={() => setLang("it")}>Italiano</DropdownMenuItem>
                  <DropdownMenuItem data-testid="lang-de" onClick={() => setLang("de")}>Deutsch</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <button onClick={() => navigate("/app/notifications")} className="relative p-2 hover:bg-zinc-100" style={{borderRadius: 2}} data-testid="bell-btn">
                <Bell size={18} />
                {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-[#E63946] rounded-full"></span>}
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
